/**
 * Hero preview for the palette editor (later the character creator): renders
 * the REAL committed hero through the game's own compositing path
 * (hero.ts `drawHero` + heroLut), so what is judged here is what ships —
 * one recolourer, no second approximation.
 *
 * Two canvases, the A/B the plan promises:
 *   - LIVE  — canon part frames + the picked look's exact hex→hex table
 *     (runtime recolour; reflects the editor's UN-saved draft swatches).
 *   - BAKED — one of the committed tint trees, the very art the LUT replaces
 *     (deletable once this comparison has made the verdict).
 *
 * Both show the same outfit, same anim, same four directions. The sim never
 * sees any of it.
 */
import { parseAtlas, type Atlas, type Dir, type Outfit } from "./atlas.js";
import { defaultOutfit, drawHero, heroOutfitBounds, outfitAnchorPx, outfitParts, partFrameImg, type HeroAssets, WALK_FRAMES } from "./hero.js";
import { packHeroLut, type HeroLut } from "./heroLut.js";
import { type PaletteCatalog, type RegionColours } from "@pd/shared";

/** The preview's 2×2 grid — front first, the direction you judge from. */
export const PREVIEW_DIRS: Dir[] = ["s", "e", "w", "n"];
const ANIMS = ["idle", "walk"] as const;
const PREVIEW_SCALE = 2;

export interface HeroPreview {
  assets: HeroAssets;
  /** slot → variant (null = unequipped). Shared by both canvases. */
  outfit: Outfit;
  /** region → swatch id (0/absent = canon). The LIVE canvas's look. */
  look: RegionColours;
  /** Baked tint name for the A/B canvas. */
  baked: string;
  /** Packed look for the LIVE canvas (rebuilt whenever the draft changes). */
  lut: HeroLut | null;
  /** True once every frame the current (outfit, baked) picks need is loaded. */
  ready: boolean;
  error: string | null;
}

/** Fetch and parse the committed atlas. Null on any failure — every caller
 *  that draws art has a no-art path, and none of them may block on it. */
export async function loadAtlas(): Promise<Atlas | null> {
  let json: unknown;
  try {
    const r = await fetch("/sprites/atlas.json");
    if (!r.ok) return null;
    json = await r.json();
  } catch {
    return null;
  }
  let parsed;
  try {
    parsed = parseAtlas(json);
  } catch {
    return null;
  }
  return parsed.ok ? parsed.atlas : null;
}

/** Stand up an empty preview from an atlas the caller already has (the auth
 *  screen loads one atlas for both its scene and its hero — one fetch, one
 *  parse, one version). */
export function previewFromAtlas(atlas: Atlas): HeroPreview | null {
  const hero = atlas.sprites.hero;
  if (!hero || hero.tile || !hero.outfit) return null;
  return {
    assets: { atlas, hero, imgCache: new Map(), lastDrawn: new Map() },
    outfit: defaultOutfit(hero.outfit),
    look: {},
    baked: "ember",
    lut: null,
    ready: false,
    error: null,
  };
}

/** Load the committed atlas and stand up an empty preview. Null on any
 *  failure — the editor keeps working, the preview pane says why. */
export async function loadPreview(): Promise<HeroPreview | null> {
  const atlas = await loadAtlas();
  return atlas ? previewFromAtlas(atlas) : null;
}

/** The baked tints the A/B canvas can wear (the art the LUT replaces). */
export function previewTints(pv: HeroPreview): string[] {
  return [...pv.assets.hero.tints];
}

/** Rebuild the LIVE canvas's packed look from the current draft. `cat` is
 *  the editor's draft catalogue (its unsaved swatches); omitted = committed. */
export function refreshLut(pv: HeroPreview, cat?: PaletteCatalog): void {
  pv.lut = packHeroLut(pv.look, cat);
}

/** Every part-frame path the two canvases need for the current picks. */
function neededPaths(pv: HeroPreview): Set<string> {
  const paths = new Set<string>();
  const o = pv.assets.hero.outfit;
  if (!o) return paths;
  for (const tint of ["canon", pv.baked]) {
    const tintFrames = o.frames[tint];
    if (!tintFrames) continue;
    for (const dir of PREVIEW_DIRS) {
      for (const p of outfitParts(pv.assets.hero, pv.outfit, dir, tint)) {
        for (const anim of ANIMS) {
          for (const fp of tintFrames[p.slot]?.[p.variant]?.[p.layer]?.[anim]?.[dir] ?? []) paths.add(fp);
        }
      }
    }
  }
  return paths;
}

/** Resolve + load every needed frame. Resolves when they are all in hand
 *  (or failed — drawHero's last-drawn fallback keeps whatever did load). */
export function ensureReady(pv: HeroPreview): Promise<void> {
  const pending: HTMLImageElement[] = [];
  for (const path of neededPaths(pv)) {
    const im = partFrameImg(pv.assets, path);
    if (!(im.complete && im.naturalWidth > 0)) pending.push(im);
  }
  return new Promise((res) => {
    if (pending.length === 0) {
      pv.ready = true;
      res();
      return;
    }
    let left = pending.length;
    const done = (): void => {
      left -= 1;
      if (left === 0) {
        pv.ready = true;
        res();
      }
    };
    for (const im of pending) {
      im.addEventListener("load", done, { once: true });
      im.addEventListener("error", done, { once: true });
    }
  });
}

/** Draw one preview canvas: the 2×2 direction grid, walk-anim, at 2× scale.
 *  `tint` + `lut` pick the mode — ("canon", lut) is the LIVE side,
 *  (bakedTint, null) the BAKED side. `tMs` drives the anim clock. */
export function drawPreview(
  cv: HTMLCanvasElement,
  pv: HeroPreview,
  tint: string,
  lut: HeroLut | null,
  tMs: number,
): void {
  const hero = pv.assets.hero;
  if (!hero) return;
  const bounds = heroOutfitBounds(hero, pv.outfit, tint);
  const [dw, dh] = bounds.dims;
  const [ax, ay] = outfitAnchorPx(hero, bounds, PREVIEW_SCALE);
  const cols = 2;
  const W = (PREVIEW_DIRS.length / cols) * dw * PREVIEW_SCALE;
  const H = cols * dh * PREVIEW_SCALE;
  if (cv.width !== W || cv.height !== H) {
    cv.width = W;
    cv.height = H;
  }
  const ctx = cv.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, W, H);
  const fps = hero.anims.walk?.fps ?? 8;
  const frame = Math.floor(tMs / (1000 / fps)) % WALK_FRAMES;
  PREVIEW_DIRS.forEach((dir, i) => {
    const ox = (i % cols) * dw * PREVIEW_SCALE;
    const oy = Math.floor(i / cols) * dh * PREVIEW_SCALE;
    drawHero(ctx, pv.assets, tint, pv.outfit, "walk", dir, frame, ox + ax, oy + ay, PREVIEW_SCALE, lut);
  });
}
